import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded.tsx';
import { NameCell } from '@openx/components-core/lib/NameCell/NameCell.tsx';
import {
  LabeledValue,
  LabeledValueComponentWithTooltip,
} from '@openx/components-core/lib/Value/LabeledValue/LabeledValue.tsx';
import { ItemsPreviewCell } from '@openx/components-table/cells/ItemsPreviewCell.tsx';
import type { TableColumn } from '@openx/types/tableHelpers.ts';
import { toPercentString } from '@openx/utils/lib/toPercentString.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { isMasterOrganisation } from '../../../../permissions/validators.ts';
import type { CuratedDealBuyerDiscountApi } from '../../../../types/curatedDealBuyerDiscount.ts';
import { useAccountOptionsFetch } from '../../hooks/useAccountOptionsFetch.ts';
import { useDemandPartnerOptionsFetch } from '../../hooks/useDemandPartnerOptionsFetch.ts';

export const useCuratedDealBuyerDiscountColumns = (searchPhrase?: string) => {
  const { session } = useAuth();
  const { t } = useTranslation();
  const { isLoading: isLoadingAccounts, mapById: accountByUid } = useAccountOptionsFetch();
  const { isLoading: isLoadingDemandPartners, mapById: demandPartnerById } = useDemandPartnerOptionsFetch();

  const isMasterOrg = isMasterOrganisation(session);

  return useMemo<TableColumn<CuratedDealBuyerDiscountApi>[]>(
    () => [
      {
        key: 'name',
        render: ({ id, name, uid }) => (
          <NameCell id={id} name={name} pathTo={`/discounts/${uid}/details`} searchPhrase={searchPhrase} />
        ),
        title: t('Name'),
        width: isMasterOrg ? '30%' : '40%',
      },
      ...(isMasterOrg
        ? [
            {
              key: 'account_uid',
              render: ({ account_uid }) => (
                <WhenLoaded loading={isLoadingAccounts} noPadding>
                  {accountByUid[account_uid]?.name ?? account_uid}
                </WhenLoaded>
              ),
              title: t('Organization'),
              width: '15%',
            },
          ]
        : []),
      {
        key: 'applied_to',
        render: ({ demand_partner_id, seat_ids }) => {
          if (typeof demand_partner_id === 'string') {
            return (
              <WhenLoaded loading={isLoadingDemandPartners} noPadding>
                <LabeledValue
                  label={t('DSP')}
                  value={demandPartnerById[demand_partner_id]?.name ?? demand_partner_id}
                  valueRender={props => <LabeledValueComponentWithTooltip {...props} />}
                />
              </WhenLoaded>
            );
          }

          return <ItemsPreviewCell items={Object.keys(seat_ids)} label={t('Seats')} />;
        },
        title: t('Applied to'),
        width: isMasterOrg ? '20%' : '25%',
      },
      {
        key: 'banner_and_video_discount',
        render: ({ banner_and_video_discount }) => toPercentString(banner_and_video_discount),
        title: t('Banner and Video'),
        width: '10%',
      },
      {
        key: 'ctv_discount',
        render: ({ ctv_discount }) => toPercentString(ctv_discount),
        title: t('CTV'),
        width: '10%',
      },
      {
        key: 'modified_date',
        isDate: true,
        isExactDate: true,
        title: t('Last Modified'),
        width: '15%',
      },
    ],
    [accountByUid, demandPartnerById, isMasterOrg, isLoadingAccounts, isLoadingDemandPartners, searchPhrase, t]
  );
};
